import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import type { AdminAccount, ViewpointSubmission } from '@whaletail/shared';
import { googleMapsPinUrl } from '@whaletail/shared';
import {
  ApiError,
  adminCheckForUpdate,
  adminCreateAccount,
  adminListAccounts,
  adminMarkSmsTrained,
  adminQueue,
  adminRerunSms,
  adminSetPassword,
  adminSetPasswordExpired,
  adminSetRole,
  adminStartUpdate,
  adminUnplacedSms,
  adminUpdateProgress,
  approveViewpoint,
  ago,
  clockDateTime,
  rejectViewpoint,
  removeViewpoint,
  type AdminQueue,
  type ReleaseCheck,
  type UnplacedSms,
  type UpdateProgress,
} from '../api.ts';

const PASSWORD_MIN = 8;
const PASSWORD_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';

/** A random password an admin can hand off, not one to remember — 16 chars from a set with no easily-confused characters. */
function generatePassword(): string {
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => PASSWORD_CHARS[b % PASSWORD_CHARS.length]).join('');
}

interface Props {
  onFlyTo: (lat: number, lon: number, zoom?: number) => void;
  /** Tells the app the queue changed, so the tab badge and the map stay current. */
  onChanged: (pendingCount: number) => void;
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

/**
 * Review queue for community view points. Pending spots are already drawn on the
 * map for admins (yellow), so "Show on map" lets you see the surroundings before
 * deciding. Approving publishes the spot to everyone; rejecting removes it from
 * the map and tells the submitter, with an optional reason.
 */
export function AdminPanel({ onFlyTo, onChanged }: Props) {
  const [queue, setQueue] = useState<AdminQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<number, string>>({});
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<number | null>(null);

  // Read the callback through a ref so `load` keeps a stable identity. The parent
  // passes a fresh inline function every render, and `onChanged` updates the
  // parent's state — if `load` depended on it directly, each fetch would re-render
  // the parent, mint a new `load`, and trigger the effect below again, forever.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    try {
      const q = await adminQueue();
      setQueue(q);
      setError(null);
      onChangedRef.current(q.pendingCount);
    } catch (e) {
      setError(errText(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (id: number, fn: () => Promise<unknown>) => {
    setBusy(id);
    setError(null);
    try {
      await fn();
      setRejecting(null);
      setReason('');
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };

  // --- texts the gazetteer could not place ------------------------------------
  const [unplaced, setUnplaced] = useState<UnplacedSms[] | null>(null);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [smsBusy, setSmsBusy] = useState<number | null>(null);

  const loadUnplaced = useCallback(async () => {
    try {
      setUnplaced(await adminUnplacedSms());
      setSmsError(null);
    } catch (e) {
      setSmsError(errText(e));
    }
  }, []);

  useEffect(() => {
    void loadUnplaced();
  }, [loadUnplaced]);

  // Outcome of the last re-run per text, shown until the list next changes.
  const [rerunNote, setRerunNote] = useState<Record<number, string>>({});

  const rerun = async (id: number) => {
    setSmsBusy(id);
    try {
      const r = await adminRerunSms(id);
      setRerunNote((n) => ({
        ...n,
        [id]: r.trained
          ? 'All placed.'
          : `${r.plotted} newly plotted, ${r.unplaced} still not placed — the place is not in the gazetteer yet.`,
      }));
      await loadUnplaced();
    } catch (e) {
      setSmsError(errText(e));
    } finally {
      setSmsBusy(null);
    }
  };

  const markTrained = async (id: number) => {
    setSmsBusy(id);
    try {
      await adminMarkSmsTrained(id);
      await loadUnplaced();
    } catch (e) {
      setSmsError(errText(e));
    } finally {
      setSmsBusy(null);
    }
  };

  // --- accounts (this server has no public sign-up; an admin creates every
  // account by hand instead — see LoginGate.tsx and MeResponse.registrationOpen) ---
  const [accounts, setAccounts] = useState<AdminAccount[] | null>(null);
  const [acctError, setAcctError] = useState<string | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAdmin, setNewAdmin] = useState(false);
  const [acctBusy, setAcctBusy] = useState(false);
  // Shown once right after creation — nowhere else keeps a plaintext copy,
  // so this is the admin's only chance to hand it off.
  const [justCreated, setJustCreated] = useState<{ username: string; password: string } | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      setAccounts(await adminListAccounts());
    } catch (e) {
      setAcctError(errText(e));
    }
  }, []);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const createAccount = async (e: FormEvent) => {
    e.preventDefault();
    setAcctError(null);
    if (newPassword.length < PASSWORD_MIN) return setAcctError(`Use at least ${PASSWORD_MIN} characters for the password.`);

    setAcctBusy(true);
    try {
      await adminCreateAccount(newUsername.trim(), newPassword, newAdmin ? 'admin' : undefined);
      setJustCreated({ username: newUsername.trim(), password: newPassword });
      setNewUsername('');
      setNewPassword('');
      setNewAdmin(false);
      await loadAccounts();
    } catch (e) {
      setAcctError(errText(e));
    } finally {
      setAcctBusy(false);
    }
  };

  // Which account row an action is in flight for — separate from acctBusy
  // (the create-account form) since a row action must not disable the form.
  const [rowBusy, setRowBusy] = useState<number | null>(null);
  const rowAct = async (id: number, fn: () => Promise<unknown>) => {
    setRowBusy(id);
    setAcctError(null);
    try {
      await fn();
      await loadAccounts();
    } catch (e) {
      setAcctError(errText(e));
    } finally {
      setRowBusy(null);
    }
  };

  /** A fresh random password an admin can hand off — the same one-time reveal banner used right after creating an account. */
  const resetPassword = (a: AdminAccount) =>
    rowAct(a.id, async () => {
      const password = generatePassword();
      await adminSetPassword(a.id, password);
      setJustCreated({ username: a.username, password });
    });

  const toggleRole = (a: AdminAccount) => rowAct(a.id, () => adminSetRole(a.id, a.role === 'admin' ? 'user' : 'admin'));

  const toggleExpired = (a: AdminAccount) => rowAct(a.id, () => adminSetPasswordExpired(a.id, !a.passwordExpired));

  // --- server updates (pulls a release from GitHub — see update.ts on the
  // server and scripts/self-update.ts, which does the actual work) ---
  const [release, setRelease] = useState<ReleaseCheck | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [progress, setProgress] = useState<UpdateProgress | null>(null);
  const [confirmingUpdate, setConfirmingUpdate] = useState(false);

  useEffect(() => {
    adminCheckForUpdate()
      .then(setRelease)
      .catch((e) => setUpdateError(errText(e)));
  }, []);

  // While an update is in flight the server restarts (briefly unreachable),
  // so a failed poll is expected mid-update, not a reason to stop polling —
  // only genuinely idle/finished states stop this interval.
  useEffect(() => {
    if (!progress || progress.state === 'idle' || progress.state === 'done' || progress.state === 'rolled-back' || progress.state === 'failed') {
      return;
    }
    const id = setInterval(() => {
      adminUpdateProgress()
        .then(setProgress)
        .catch(() => undefined);
    }, 1500);
    return () => clearInterval(id);
  }, [progress]);

  const startUpdate = async () => {
    setConfirmingUpdate(false);
    setUpdateError(null);
    try {
      await adminStartUpdate();
      setProgress({ state: 'waiting-for-old-process' });
    } catch (e) {
      setUpdateError(errText(e));
    }
  };

  if (!queue) {
    return <div className="admin-page-content">{error ? <p className="form-error pad">{error}</p> : <div className="empty">Loading…</div>}</div>;
  }

  const Coords = ({ s }: { s: ViewpointSubmission }) => (
    <span className="item-meta">
      {s.lat.toFixed(5)}, {s.lon.toFixed(5)}
    </span>
  );

  return (
    <div className="admin-page-content">
      {error && <p className="form-error pad">{error}</p>}

      <section className="section">
        <h3>
          Awaiting review <span className="count-pill">{queue.pending.length}</span>
        </h3>
        {queue.pending.length === 0 && <p className="muted">Nothing to review. 🎉</p>}
        <ul className="item-list">
          {queue.pending.map((s) => {
            const name = names[s.id] ?? s.name;
            return (
              <li key={s.id} className="item item-col review">
                <label className="field">
                  <span>Name (you can correct it before approving)</span>
                  <input value={name} onChange={(e) => setNames((n) => ({ ...n, [s.id]: e.target.value }))} maxLength={80} />
                </label>
                <div className="item-meta">
                  {s.region && <>{s.region} · </>}
                  {s.hike && <>hike required · </>}
                  suggested by <strong>{s.submittedBy ?? 'a deleted account'}</strong>, {ago(Date.now() - s.createdAt)}
                </div>
                {s.note && <div className="review-note">“{s.note}”</div>}
                <Coords s={s} />
                <div className="item-actions">
                  <button type="button" className="link-btn" onClick={() => onFlyTo(s.lat, s.lon, 15)}>
                    Show on Map
                  </button>
                  <a className="link-btn" href={googleMapsPinUrl(s.lat, s.lon)} target="_blank" rel="noopener noreferrer">
                    Google Maps
                  </a>
                </div>

                {rejecting === s.id ? (
                  <div className="stack">
                    <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional — the submitter sees this)" maxLength={300} aria-label="Reason for rejecting" autoFocus />
                    <div className="row gap">
                      <button type="button" className="btn btn-danger" disabled={busy === s.id} onClick={() => void act(s.id, () => rejectViewpoint(s.id, reason.trim()))}>
                        Confirm Reject
                      </button>
                      <button type="button" className="btn" onClick={() => setRejecting(null)}>
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="row gap">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy === s.id || name.trim().length < 2}
                      onClick={() => void act(s.id, () => approveViewpoint(s.id, name.trim() !== s.name ? { name: name.trim() } : {}))}
                    >
                      Approve
                    </button>
                    <button type="button" className="btn" onClick={() => setRejecting(s.id)}>
                      Reject
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="section">
        <h3>
          Unplaced Texts <span className="count-pill">{unplaced?.length ?? 0}</span>
        </h3>
        <p className="muted">
          Orca Network texts with a report naming no place the map knows yet, so it was left off the map. Run <code>/orcatext</code> with the
          text to teach the server the place, then Re-run it to plot what is now placeable. Mark Trained clears it without re-running.
        </p>
        {smsError && <p className="form-error">{smsError}</p>}
        {unplaced && unplaced.length === 0 && <p className="muted">Nothing waiting. 🎉</p>}
        <ul className="item-list">
          {unplaced?.map((m) => (
            <li key={m.id} className="item item-col review">
              <div className="item-meta">Received {clockDateTime(m.receivedAt)}</div>
              <div className="review-note">{m.rawText}</div>
              {m.unplacedReports.length > 0 && (
                <div className="item-meta">
                  Not placed:
                  {m.unplacedReports.map((r, i) => (
                    <div key={i}>“{r}”</div>
                  ))}
                </div>
              )}
              {rerunNote[m.id] && <div className="item-meta">{rerunNote[m.id]}</div>}
              <div className="row gap">
                <button type="button" className="btn btn-primary" disabled={smsBusy === m.id} onClick={() => void rerun(m.id)}>
                  Re-run
                </button>
                <button type="button" className="btn" disabled={smsBusy === m.id} onClick={() => void markTrained(m.id)}>
                  Mark Trained
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="section">
        <h3>
          Community spots on the map <span className="count-pill">{queue.approved.length}</span>
        </h3>
        {queue.approved.length === 0 ? (
          <p className="muted">None yet. The built-in Orca Network spots are not listed here.</p>
        ) : (
          <ul className="item-list">
            {queue.approved.map((s) => (
              <li key={s.id} className="item item-col">
                <div className="item-head">
                  <span className="item-title">{s.name}</span>
                </div>
                <div className="item-meta">
                  {s.region && <>{s.region} · </>}by {s.submittedBy ?? 'a deleted account'}
                </div>
                <div className="item-actions">
                  <button type="button" className="link-btn" onClick={() => onFlyTo(s.lat, s.lon, 15)}>
                    Show
                  </button>
                  <button type="button" className="link-btn danger" disabled={busy === s.id} onClick={() => void act(s.id, () => removeViewpoint(s.id))}>
                    Remove from Map
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section">
        <h3>
          Accounts <span className="count-pill">{accounts?.length ?? '…'}</span>
        </h3>
        <p className="muted">This server has no public sign-up — create an account here and hand off the password yourself.</p>

        {acctError && <p className="form-error">{acctError}</p>}

        {justCreated && (
          <div className="review-note">
            <strong>{justCreated.username}</strong> was created with password <code>{justCreated.password}</code>. This is
            shown once — copy it now.
            <div className="item-actions">
              <button
                type="button"
                className="link-btn"
                onClick={() => void navigator.clipboard.writeText(justCreated.password)}
              >
                Copy Password
              </button>
              <button type="button" className="link-btn" onClick={() => setJustCreated(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        <form onSubmit={(e) => void createAccount(e)} className="stack">
          <label className="field">
            <span>Username</span>
            <input
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              autoCapitalize="none"
              spellCheck={false}
              required
              minLength={3}
              maxLength={32}
            />
          </label>
          <label className="field">
            <span>Password</span>
            <div className="row gap">
              <input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                maxLength={200}
              />
              <button type="button" className="btn btn-small" onClick={() => setNewPassword(generatePassword())}>
                Generate
              </button>
            </div>
            <small>At least {PASSWORD_MIN} characters.</small>
          </label>
          <label className="check">
            <input type="checkbox" checked={newAdmin} onChange={(e) => setNewAdmin(e.target.checked)} />
            Make This Account an Admin Too
          </label>
          <button type="submit" className="btn btn-primary" disabled={acctBusy}>
            {acctBusy ? 'Creating…' : 'Create Account'}
          </button>
        </form>

        {accounts && accounts.length > 0 && (
          <ul className="item-list">
            {accounts.map((a) => (
              <li key={a.id} className="item">
                <div className="item-head">
                  <span className="item-title">{a.username}</span>
                  {a.role === 'admin' && <span className="count-pill">admin</span>}
                  {a.passwordExpired && (
                    <span className="count-pill count-alert" title="Must set a new password before using the app again">
                      expired
                    </span>
                  )}
                </div>
                <div className="item-meta">Created {clockDateTime(a.createdAt)}</div>
                <div className="item-actions">
                  <button type="button" className="link-btn" disabled={rowBusy === a.id} onClick={() => void resetPassword(a)}>
                    Reset Password
                  </button>
                  <button type="button" className="link-btn" disabled={rowBusy === a.id} onClick={() => void toggleRole(a)}>
                    {a.role === 'admin' ? 'Make user' : 'Make admin'}
                  </button>
                  <button type="button" className="link-btn" disabled={rowBusy === a.id} onClick={() => void toggleExpired(a)}>
                    {a.passwordExpired ? 'Cancel expiry' : 'Expire password'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section">
        <h3>Server</h3>

        {/* updateError is a failed request (network, 403, ...); release.error
            is a normal 200 response saying why it couldn't check GitHub
            (e.g. not configured) or why the release it found isn't usable —
            distinct cases, both worth showing. */}
        {updateError && !progress && <p className="form-error">{updateError}</p>}

        {release && !progress && (
          <>
            <p className="muted">
              Running <strong>{release.currentVersion}</strong>.{' '}
              {release.updateAvailable
                ? `${release.latestVersion} is available.`
                : release.latestVersion
                  ? 'Up to date.'
                  : null}
            </p>
            {release.error && <p className="muted">{release.error}</p>}
            {release.updateAvailable &&
              (confirmingUpdate ? (
                <div className="stack">
                  <p className="muted">
                    This restarts the server — a brief outage, a few seconds to a minute. A failed update rolls back
                    automatically.
                  </p>
                  <div className="row gap">
                    <button type="button" className="btn btn-primary" onClick={() => void startUpdate()}>
                      Confirm update to {release.latestVersion}
                    </button>
                    <button type="button" className="btn" onClick={() => setConfirmingUpdate(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn btn-primary" onClick={() => setConfirmingUpdate(true)}>
                  Update to {release.latestVersion}
                </button>
              ))}
          </>
        )}

        {progress && (
          <p className={progress.state === 'failed' ? 'form-error' : 'muted'} role="status" aria-live="polite">
            {
              {
                'waiting-for-old-process': 'Stopping the current server…',
                'backing-up': 'Backing up the current version…',
                downloading: 'Downloading the update…',
                staged: 'Downloaded — restarting the server to apply it…',
                extracting: 'Extracting…',
                installing: 'Installing dependencies…',
                starting: 'Starting the new version…',
                verifying: 'Waiting for it to come up…',
                'rolling-back': 'That failed — rolling back to the previous version…',
                done: `Updated to ${progress.version}. Refresh the page.`,
                'rolled-back': `The update failed and was rolled back — still running ${release?.currentVersion ?? 'the previous version'}.`,
                failed: 'The update failed, and the automatic rollback also failed. This needs a look on the server itself.',
              }[progress.state] ?? progress.state
            }
            {progress.error && (progress.state === 'rolled-back' || progress.state === 'failed') && (
              <>
                <br />
                <span className="muted">{progress.error}</span>
              </>
            )}
          </p>
        )}
      </section>
    </div>
  );
}
